import { Box, Typography } from '@mui/material';
import { daysInCurrentMonth } from '@alluradirect/react';
import React, { useEffect } from 'react';
import { AppFormLabel } from 'components/core/AppFormLabel/AppFormLabel';
import { FormikAppSelect } from 'components/formik/core/FormikAppSelect/FormikAppSelect';
import { useField } from 'formik';
import Grid from 'components/core/Grid';
import { MONTHS_OF_YEAR } from 'components/formik/core/FormikDateOfBirth/constants';
import dayjs from 'dayjs';

interface Props {
  fieldPrefix: string;

  title?: string;
  description?: string;

  minOwnerAge?: number;
}

/** Requires formik values to include an object [objkey]: { YEAR: number, MONTH: number; DAY: number }
 *
 * Will set a field DATE_OF_BIRTH with the combined values as a string, as well as YEAR, MONTH, DAY fields
 */
const FormikDateOfBirth = ({ fieldPrefix, title, description, minOwnerAge = 21 }: Props) => {
  const [yearField] = useField(`${fieldPrefix}.YEAR`);
  const [monthField] = useField(`${fieldPrefix}.MONTH`);
  const [dayField] = useField(`${fieldPrefix}.DAY`);
  const [, , helpers] = useField(`DATE_OF_BIRTH`);

  const years = React.useMemo(() => {
    const currentYear = Number(new Date().getFullYear().toString());
    const minOwnerAgeYear = currentYear - minOwnerAge;
    const totalYears = 101 - minOwnerAge;

    return Array.from(Array(totalYears).keys()).map((_el, index) => {
      const y = minOwnerAgeYear - index;

      return { value: y, label: y.toString() };
    });
  }, [minOwnerAge]);

  const months = React.useMemo(() => {
    return MONTHS_OF_YEAR.map((month, ind) => {
      return {
        label: month,
        value: ind + 1,
      };
    });
  }, []);

  const days = React.useMemo(() => {
    const days = daysInCurrentMonth(parseInt(yearField.value), monthField.value);

    if (!days) return [];

    return Array.from(Array(days).keys()).map(day => {
      return {
        label: (day + 1).toString(),
        value: day + 1,
      };
    });
  }, [monthField.value, yearField.value]);

  // Synchronize DATE_OF_BIRTH field with YEAR, MONTH, DAY values
  useEffect(() => {
    if (yearField.value && monthField.value && dayField.value) {
      const month = minTwoDigits(monthField.value);
      const day = minTwoDigits(dayField.value);

      helpers.setValue(`${yearField.value}-${month}-${day}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayField.value, monthField.value, yearField.value]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box marginBottom={title || description ? '8px' : '0px'}>
          {title ? <AppFormLabel title={title} /> : null}
          {description ? <Typography variant="body2">{description}</Typography> : null}
        </Box>
      </Grid>

      <Grid item xs={12} sm={4}>
        <FormikAppSelect name={`${fieldPrefix}.YEAR`} options={years} label="Year" />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormikAppSelect name={`${fieldPrefix}.MONTH`} options={months} label="Month" disabled={!yearField.value} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormikAppSelect
          name={`${fieldPrefix}.DAY`}
          options={days}
          label="Day"
          disabled={!monthField.value || !yearField.value}
        />
      </Grid>
    </Grid>
  );
};

function minTwoDigits(n: string | number) {
  return (n < 10 ? '0' : '') + n;
}

export function generateDOB(dateOfBirth: string) {
  const dob = dateOfBirth ? dayjs(dateOfBirth) : undefined;
  return {
    DOB: {
      YEAR: dob ? dob.year().toString() : '',
      MONTH: dob ? (dob.month() + 1).toString() : '',
      DAY: dob ? dob.date().toString() : '',
    },
  };
}

export function generateInitialDateOfBirth(
  year: string | undefined | number,
  month: string | undefined | number,
  day: string | undefined | number,
): string {
  if (!year || !month || !day) return '';
  const monthTwoDigits = minTwoDigits(month);
  const dayTwoDigits = minTwoDigits(day);
  return `${year}-${monthTwoDigits}-${dayTwoDigits}`;
}

export default FormikDateOfBirth;
